import {
  getReadonlyListItemCurrency,
  getReadonlyListItemDate,
  getReadonlyListItemId,
  getReadonlyListItemNumber,
  getReadonlyListItemPill,
  getReadonlyListItemString,
  getReadonlyListItemTranslatedString,
} from '@interxfi/app-components/components/readonly-list/util';
import type { ReadonlyListItemRecord } from '@interxfi/design-system/components/list';
import type {
  IxcCorpactionsCaadminV1DtcPaymentHeader,
  IxcCorpactionsCaadminV1DtcPaymentTransaction,
  IxcCorpactionsCaadminV1PaymentAmount,
  IxcCorpactionsCaadminV1PaymentData,
  IxcCorpactionsCaadminV1PaymentDtc,
  IxcCorpactionsCaadminV1PaymentState,
} from '@interxfi/types';
import { getNumericStringOrUndefinedFromNullable } from '@interxfi/utilities/object/access';
import { getAssetDetailPath } from '../../../asset/asset-routing';
import { getJ5StateMetadataListItems } from '../../../j5/state/j5-state-list-items';
import { getCorpactHeaderListItems } from '../../corpact-shared-list-items';
import { getCorpactCaAdminActionDetailPath } from '../action/ca-admin-action-routing';
import { CorpactCaAdminPaymentStatusPill } from './ca-admin-payment-status-pill';

export function getCorpactCaAdminDtcPaymentHeaderListItems(data: IxcCorpactionsCaadminV1DtcPaymentHeader | undefined, prefix = '') {
  return {
    eventId: getReadonlyListItemId(`${prefix}eventId`, 'Event ID', data?.eventId),
    underlyingCusip: getReadonlyListItemString(`${prefix}underlyingCusip`, 'Underlying CUSIP', data?.underlyingCusip),
    paymentId: getReadonlyListItemId(`${prefix}paymentId`, 'Payment ID', data?.paymentId),
  } as const satisfies ReadonlyListItemRecord;
}

export function getCorpactCaAdminPaymentDtcListItems(data: IxcCorpactionsCaadminV1PaymentDtc | undefined, prefix = '') {
  return {
    dtcHeader: getCorpactCaAdminDtcPaymentHeaderListItems(data?.dtcHeader, `${prefix}dtcHeader.`),
    corporateActionEventIdentification: getReadonlyListItemId(
      `${prefix}corporateActionEventIdentification`,
      'Corporate Action Event ID',
      data?.corporateActionEventIdentification,
    ),
    officialCorporateActionEventIdentification: getReadonlyListItemId(
      `${prefix}officialCorporateActionEventIdentification`,
      'Official Corporate Action Event ID',
      data?.officialCorporateActionEventIdentification,
    ),
    previousMovementPreliminaryAdviceIdentification: getReadonlyListItemId(
      `${prefix}previousMovementPreliminaryAdviceIdentification`,
      'Previous Movement Preliminary Advice ID',
      data?.previousMovementPreliminaryAdviceIdentification,
    ),
    previousMovementConfirmationIdentification: getReadonlyListItemId(
      `${prefix}previousMovementConfirmationIdentification`,
      'Previous Movement Confirmation ID',
      data?.previousMovementConfirmationIdentification,
    ),
    movementConfirmationIdentification: getReadonlyListItemId(
      `${prefix}movementConfirmationIdentification`,
      'Movement Confirmation ID',
      data?.movementConfirmationIdentification,
    ),
    lastProcessingStatusAt: getReadonlyListItemDate(
      `${prefix}lastProcessingStatusAt`,
      'Last Processing Status At',
      data?.lastProcessingStatusAt,
      'timestamp_iso_ms',
    ),
  } as const satisfies ReadonlyListItemRecord;
}

export function getCorpactCaAdminPaymentAmountListItems(data: IxcCorpactionsCaadminV1PaymentAmount | undefined, prefix = '') {
  return {
    value: getReadonlyListItemNumber(`${prefix}value`, 'Value', getNumericStringOrUndefinedFromNullable(data, 'value')),
    currency: getReadonlyListItemString(`${prefix}currency`, 'Currency', data?.currency),
    formatted: getReadonlyListItemCurrency(`${prefix}summary`, 'Value', data?.value, { currency: data?.currency || 'USD' }),
  } as const satisfies ReadonlyListItemRecord;
}

export function getCorpactCaAdminPaymentDataListItems(data: IxcCorpactionsCaadminV1PaymentData | undefined, prefix = '') {
  return {
    amount: getCorpactCaAdminPaymentAmountListItems(data?.amount, `${prefix}amount.`),
    expectedPaymentDate: getReadonlyListItemDate(`${prefix}expectedPaymentDate`, 'Expected Payment Date', data?.expectedPaymentDate, 'date_iso'),
    dtc: getCorpactCaAdminPaymentDtcListItems(data?.dtc, `${prefix}dtc.`),
  } as const satisfies ReadonlyListItemRecord;
}

export function getCorpactCaAdminPaymentStateListItems(data: IxcCorpactionsCaadminV1PaymentState | undefined) {
  return {
    metadata: getJ5StateMetadataListItems(data?.metadata),
    paymentId: getReadonlyListItemId('paymentId', 'Payment ID', data?.paymentId),
    actionId: getReadonlyListItemId('actionId', 'Action ID', data?.actionId, getCorpactCaAdminActionDetailPath(data?.actionId)),
    assetId: getReadonlyListItemId('assetId', 'Asset ID', data?.assetId, getAssetDetailPath(data?.assetId)),
    status: getReadonlyListItemTranslatedString('status', 'Status', {
      namespace: 'ixcCorpactionsCaadminV1',
      enumName: 'IxcCorpactionsCaadminV1PaymentStatus',
      value: data?.status,
    }),
    statusPill: getReadonlyListItemPill('statusPill', 'Status', data?.status ? <CorpactCaAdminPaymentStatusPill status={data.status} /> : undefined),
    data: getCorpactCaAdminPaymentDataListItems(data?.data, 'data.'),
  } as const satisfies ReadonlyListItemRecord;
}

export function getCorpactCaAdminDtcPaymentTransactionListItems(data: IxcCorpactionsCaadminV1DtcPaymentTransaction | undefined, prefix = '') {
  return {
    actionId: getReadonlyListItemId(`${prefix}actionId`, 'Action ID', data?.actionId, getCorpactCaAdminActionDetailPath(data?.actionId)),
    paymentAdviceId: getReadonlyListItemId(`${prefix}paymentAdviceId`, 'Payment Advice ID', data?.paymentAdviceId),
    header: getCorpactHeaderListItems(data?.header, `${prefix}header.`),
  } as const satisfies ReadonlyListItemRecord;
}
